// /english/pronunciation — the pronunciation drill.
//
// One word at a time: read it aloud, reveal the IPA, grade yourself. The queue
// arrives already ordered (never tested first, then the ones you miss most), so
// this screen never sorts anything — it walks the list the backend handed it.
//
// The whole queue is fetched once and walked client-side. A round-trip between
// every card would be felt in a drill you get through at a card every few
// seconds, and the ordering only needs to be right at the moment you sit down:
// re-ordering mid-run would move a word you just missed to the front and show it
// to you again three cards later.

import { useCallback, useEffect, useState } from "react";
import CoachShell from "../components/english/CoachShell";
import RetiredWords from "../components/english/RetiredWords";
import WordCard from "../components/english/WordCard";
import {
  fetchVocab,
  restoreWord,
  retireWord,
  voteWord,
} from "../services/englishApi";

const QUEUE_SIZE = 200;

export default function EnglishPronunciationPage() {
  const [queue, setQueue] = useState([]);
  const [retired, setRetired] = useState([]);
  const [counts, setCounts] = useState({ due: 0, total: 0, retired: 0 });
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [graded, setGraded] = useState(0);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const load = useCallback(() => {
    setStatus("loading");
    fetchVocab(QUEUE_SIZE, true)
      .then((data) => {
        setQueue(data.words);
        setRetired(data.retired);
        setCounts(data.counts);
        setIndex(0);
        setRevealed(false);
        setGraded(0);
        setError(null);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, []);

  useEffect(load, [load]);

  const card = queue[index];

  const advance = useCallback(() => {
    setRevealed(false);
    setConfirmRemove(false);
    setIndex((i) => i + 1);
  }, []);

  // Optimistic: the count on the card updates now and the POST catches up. A
  // failed vote is worth a message but not worth blocking the drill, so the
  // rollback puts the card back at the end of the queue rather than freezing it.
  const vote = useCallback(
    (verdict) => {
      if (!card) return;
      const { word } = card;
      setQueue((rows) =>
        rows.map((row, i) =>
          i === index
            ? {
                ...row,
                good: row.good + (verdict === "good" ? 1 : 0),
                wrong: row.wrong + (verdict === "wrong" ? 1 : 0),
                attempts: row.attempts + 1,
              }
            : row,
        ),
      );
      setGraded((n) => n + 1);
      advance();
      voteWord(word, verdict).catch(() => {
        setGraded((n) => n - 1);
        setError(`Could not save your vote on "${word}" — it will come round again.`);
      });
    },
    [card, index, advance],
  );

  const remove = useCallback(() => {
    if (!card) return;
    const { word } = card;
    advance();
    retireWord(word)
      .then((data) => {
        setCounts(data.counts);
        setRetired((rows) => [...rows, { ...card, retired: true }]);
      })
      .catch(() => setError(`Could not remove "${word}".`));
  }, [card, advance]);

  const restore = useCallback((word) => {
    setRetired((rows) => rows.filter((row) => row.word !== word));
    restoreWord(word)
      .then((data) => setCounts(data.counts))
      .catch(() => setError(`Could not restore "${word}".`));
  }, []);

  // Same hand position as the buttons: reveal with the thumb, grade with a
  // finger. Ignored while typing, so the retired list stays usable.
  useEffect(() => {
    if (status !== "ready" || !card) return undefined;
    const onKey = (event) => {
      if (event.target.tagName === "INPUT" || event.target.tagName === "TEXTAREA") return;
      if (event.code === "Space") {
        event.preventDefault();
        setRevealed(true);
      } else if (revealed && event.key === "ArrowLeft") {
        vote("wrong");
      } else if (revealed && event.key === "ArrowRight") {
        vote("good");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [status, card, revealed, vote]);

  return (
    <CoachShell
      back={{ to: "/english", label: "English" }}
      title="Pronunciation"
      subtitle={
        status === "ready"
          ? `${counts.due} in the drill · ${counts.total} in the bank${counts.retired ? ` · ${counts.retired} removed` : ""}`
          : "Loading…"
      }
    >
      {status === "loading" && <p className="text-sm text-slate-500">Loading words…</p>}

      {status === "error" && (
        <p className="text-sm text-rose-400">
          Could not load the word bank — the backend did not answer.
        </p>
      )}

      {error && <p className="mb-4 text-xs text-rose-400">{error}</p>}

      {status === "ready" && !queue.length && (
        <p className="text-sm text-slate-500">
          No words in the drill. Generate an IPA file for a story, then run{" "}
          <code>python -m scripts.build_ipa_bank</code> to add its words to{" "}
          <code>english/US_IPA.json</code>.
        </p>
      )}

      {status === "ready" && card && (
        <>
          <div className="mx-auto max-w-xl">
            <p className="mb-3 text-center text-[11px] tabular-nums text-slate-600">
              {index + 1} / {queue.length}
            </p>
            <WordCard
              card={card}
              revealed={revealed}
              onReveal={() => setRevealed(true)}
              onVote={vote}
              onRetire={() => setConfirmRemove(true)}
            />
            {/* Inline, not window.confirm: a browser modal blocks the extension
                the E2E tests drive, and freezes the page if one is left open. */}
            {confirmRemove && (
              <div className="mt-4 flex items-center justify-center gap-3 rounded-lg border border-rose-500/30 bg-rose-500/5 px-4 py-3">
                <span className="text-xs text-slate-300">
                  Remove <strong>{card.word}</strong> from every lesson's drill?
                </span>
                <button
                  type="button"
                  onClick={remove}
                  className="text-xs font-medium text-rose-300 transition hover:text-rose-200"
                >
                  Remove
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmRemove(false)}
                  className="text-xs text-slate-500 transition hover:text-slate-300"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {status === "ready" && queue.length > 0 && !card && (
        <div className="mx-auto max-w-xl rounded-2xl border border-slate-800 bg-slate-900/40 p-10 text-center">
          <p className="text-lg font-semibold text-slate-100">Done for now</p>
          <p className="mt-2 text-sm text-slate-500">
            {graded} word{graded === 1 ? "" : "s"} graded this round.
          </p>
          <button
            type="button"
            onClick={load}
            className="mt-6 rounded-lg border border-slate-700 px-5 py-2.5 text-sm font-medium text-slate-300 transition hover:border-sky-500/50 hover:text-sky-300"
          >
            Start again
          </button>
        </div>
      )}

      {status === "ready" && <RetiredWords words={retired} onRestore={restore} />}

      <p className="mt-10 text-xs text-slate-600">
        Words and their transcriptions come from <code>english/US_IPA.json</code>.
        Scores and removals are global — they follow the word, not the lesson.
      </p>
    </CoachShell>
  );
}
